import React from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { IEcommerceQuestionSchema } from '../../types'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects'
import style from './Footer.module.scss'
import commonStyle from '../../style.module.scss'

interface IFooterProps {
  showCart: boolean
  totals: number[]
  findability: boolean
  schema: IEcommerceQuestionSchema
  renderError: () => React.ReactNode
  checkout: () => void
  onCartOpen: () => void
  onHomeClick: () => void
  showHint: () => void
  closeSimulation: () => void
}

const Footer = ({
  showCart,
  totals,
  findability,
  checkout,
  schema,
  renderError,
  onCartOpen,
  onHomeClick,
  showHint,
  closeSimulation
}: IFooterProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)
  return (
    <>
      {viewport.mobile ? (
        <footer className={style.footerMobile}>
          {showCart && (
            <>
              {renderError()}
              {totals[0] > 0 ? (
                <button
                  className={`${style.checkout} ${!valid ? style.disabled : ''}`}
                  onClick={() => {
                    hapticEffects.selection()
                    checkout()
                  }}
                >
                  {t('questions', 'ec', 'nike', 'checkout')}
                </button>
              ) : (
                <button className={style.checkout} onClick={onHomeClick}>
                  {t('questions', 'ec', 'nike', 'shopNow')}
                </button>
              )}
            </>
          )}
          <div className={style.nav}>
            <button className={style.footerItem} onClick={onHomeClick}>
              <div className={style.homeIcon} />
              <span className={style.itemText}>{t('questions', 'ec', 'nike', 'home')}</span>
            </button>
            <button className={style.footerItem} onClick={onHomeClick}>
              <div className={style.shopIcon} />
              <span className={style.itemText}>{t('questions', 'ec', 'nike', 'shop')}</span>
            </button>
            {findability ? (
              <EmptyLink color="#111">
                <div className={style.footerItem}>
                  <div className={style.bagIcon} />
                  <span className={style.itemText}>{t('questions', 'ec', 'nike', 'bag')}</span>
                </div>
              </EmptyLink>
            ) : (
              <button className={style.footerItem} onClick={onCartOpen}>
                <div className={style.bagIcon}>
                  <div className={style.amount}>{totals[0] > 99 ? '99+' : totals[0]}</div>
                </div>
                <span className={style.itemText}>{t('questions', 'ec', 'nike', 'bag')}</span>
              </button>
            )}
            <div className={`${style.footerItem} ${style.scaled}`}>
              <div className={`${commonStyle.IconAytmPlace}`}>
                <i className=" icon-aytmfd-help" onClick={showHint} />
              </div>
            </div>
            <div className={`${style.footerItem} ${style.scaled}`}>
              <div className={`${commonStyle.IconAytmPlace}`}>
                <i className=" icon-aytmfd-exit" onClick={closeSimulation} />
              </div>
            </div>
          </div>
        </footer>
      ) : (
        <footer className={style.footer}>
          <div className={style.links}>
            <div className={style.column}>
              <div className={style.title}>{t('questions', 'ec', 'nike', 'resourses')}</div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'giftCards')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'findStore')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'membership')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'journal')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'siteFeedback')}</EmptyLink>
              </div>
            </div>
            <div className={style.column}>
              <div className={style.title}>{t('questions', 'ec', 'nike', 'help')}</div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'getHelp')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'orderStatus')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'shipAndDelivery')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'returns')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'orderCancel')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'paymentOptions')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'giftBalance')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'contact')}</EmptyLink>
              </div>
            </div>
            <div className={style.column}>
              <div className={style.title}>{t('questions', 'ec', 'nike', 'company')}</div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'about')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'news')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'careers')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'investors')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'purpose')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'sustainability')}</EmptyLink>
              </div>
            </div>
            <div className={style.column}>
              <div className={style.title}>{t('questions', 'ec', 'nike', 'promosAndDiscounts')}</div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'student')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'military')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'teacher')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'professional')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'nike', 'birthday')}</EmptyLink>
              </div>
            </div>
          </div>
          <div className={style.privacy}>
            <EmptyLink color="#111">
              <div className={style.item}>{t('questions', 'ec', 'nike', 'allRights')}</div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>
                {t('questions', 'ec', 'nike', 'guides')}
                <div className={style.downIcon} />
              </div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>{t('questions', 'ec', 'nike', 'termsOfSale')}</div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>{t('questions', 'ec', 'nike', 'termsOfUse')}</div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>{t('questions', 'ec', 'nike', 'privacy')}</div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>
                <div className={style.image} />
                {t('questions', 'ec', 'nike', 'privacyChoice')}
              </div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>{t('questions', 'ec', 'nike', 'supplyChains')}</div>
            </EmptyLink>
          </div>
        </footer>
      )}
    </>
  )
}
export default Footer
